import React, { useState, useEffect, useLayoutEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Package, Share2 } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { handleSquareError } from '../../secret/lib/square'
import { EnterpriseToast } from '../../secret/components/ui/EnterpriseToast'
import { toast } from 'sonner'

import ReservationTimer from '../../secret/components/checkout/ReservationTimer'
import WholesalePaymentForm from '../components/WholesalePaymentForm'
import WholesaleKitSelector from '../components/WholesaleKitSelector'
import WholesaleSpecsTable from '../components/WholesaleSpecsTable'
import WholesaleShippingEstimator from '../components/WholesaleShippingEstimator'
import WholesaleFaq from '../components/WholesaleFaq'
import WholesaleOrderSummary from '../components/WholesaleOrderSummary'
import Footer from '../../components/Footer'

import { useWholesale } from '../contexts/WholesaleContext'
import { useTastePayment } from '../../taste/contexts/TastePaymentContext'
import useAsset from '../../hooks/useAsset'
import { WHOLESALE } from '../config/wholesale'

// US States for shipping address
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
]

const WholesalePage: React.FC = () => {
  const navigate = useNavigate()
  const {
    selectedKit,
    quote,
    fetchQuote,
    clearQuote,
    quoteLoading,
    totalAmount,
    quantity,
    couponCode,
    deliveryMethod,
    customerEmail,
    setCustomerEmail,
    customerName,
    setCustomerName,
    shippingAddress,
    setShippingAddress,
    reservation,
    createReservation,
  } = useWholesale()

  const { initializePayment, processCardPayment, processApplePayment, processGooglePayment, setError: setPaymentError } = useTastePayment()

  // Cache-busted asset URLs
  const logoWhite = useAsset('/tailtations-white.svg')
  const repeatBg = useAsset('/repeat-bg.png')
  const heroImage = useAsset('/images/taste/04-hero-gold-flake.jpg')
  const prepImage = useAsset('/images/taste/01-prep-batter-dip.jpg')
  const cookImage = useAsset('/images/taste/02-cook-fryer-action.jpg')
  const preserveImage = useAsset('/images/taste/03-preserve-blast-chiller.jpg')
  const offerImage = useAsset('/images/wholesale.jpg')

  const [step, setStep] = useState<'select' | 'details' | 'payment' | 'success'>('select')
  const [orderNumber, setOrderNumber] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isCreatingReservation, setIsCreatingReservation] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [addressErrors, setAddressErrors] = useState<Record<string, string>>({})

  // Force dark mode on <html> so :root:not(.dark) nuclear CSS rules don't fire
  useLayoutEffect(() => {
    const root = document.documentElement
    const wasDark = root.classList.contains('dark')
    root.classList.add('dark')
    return () => {
      if (!wasDark) {
        root.classList.remove('dark')
      }
    }
  }, [])

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [step])

  // Refresh the live shipping + tax quote whenever the kit or
  // destination (state / ZIP) changes while on the details step.
  useEffect(() => {
    if (step !== 'details') return
    fetchQuote()
  }, [step, selectedKit?.size, shippingAddress.state, shippingAddress.zip, quantity, couponCode, deliveryMethod, fetchQuote])

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Wholesale ships to all 50 states + DC (no restrictions unlike the consumer campaign)
  const isShipRestricted = (_state: string, _zip: string): boolean => {
    return false
  }

  // Validate shipping address
  const validateAddress = (): boolean => {
    const errors: Record<string, string> = {}
    if (!shippingAddress.street.trim()) errors.street = 'Street address is required'
    if (!shippingAddress.city.trim()) errors.city = 'City is required'
    if (!shippingAddress.state) errors.state = 'State is required'
    if (!shippingAddress.zip.trim()) errors.zip = 'ZIP code is required'
    else if (!/^\d{5}$/.test(shippingAddress.zip.trim())) errors.zip = 'ZIP must be 5 digits'
    else if (isShipRestricted(shippingAddress.state, shippingAddress.zip)) {
      errors.zip = 'Shipping restricted for this destination.'
    }
    setAddressErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle continue to details
  const handleContinueToDetails = () => {
    if (!selectedKit) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title="Kit Required"
          message="Please select the wholesale kit"
          type="error"
        />
      ))
      return
    }
    setStep('details')
  }

  // Handle continue to payment
  const handleContinueToPayment = async () => {
    if (!customerEmail) {
      setEmailError('Email is required for your order confirmation')
      document.getElementById('email-input-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    if (!validateEmail(customerEmail)) {
      setEmailError('Please enter a valid email address')
      document.getElementById('email-input-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    if (!customerName.trim()) {
      setEmailError('Full name is required')
      return
    }

    if (!validateAddress()) {
      return
    }

    setEmailError(null)
    setIsCreatingReservation(true)

    try {
      await createReservation(totalAmount)
      setStep('payment')
    } catch (error: any) {
      console.error('Error creating reservation:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title="Reservation Failed"
          message={error.message || 'Failed to create reservation. Please try again.'}
          type="error"
        />
      ))
    } finally {
      setIsCreatingReservation(false)
    }
  }

  // Initialize payment form when entering payment step
  useEffect(() => {
    if (step === 'payment' && reservation) {
      const timeoutId = setTimeout(() => {
        initializePayment('wholesale-card-container', Math.round(totalAmount * 100))
          .catch(error => {
            console.error('Payment initialization failed:', error)
            toast.custom((t) => (
              <EnterpriseToast
                t={t}
                title="Payment Form Error"
                message="Failed to initialize payment form. Please try again."
                type="error"
              />
            ))
          })
      }, 100)

      return () => clearTimeout(timeoutId)
    }
  }, [step, reservation, totalAmount, initializePayment])

  // Handle reservation expiration
  const handleReservationExpire = () => {
    toast.custom((t) => (
      <EnterpriseToast
        t={t}
        title="Reservation Expired"
        message="Your reservation has expired. Please try again."
        type="error"
      />
    ))
    clearQuote()
    setStep('select')
  }

  // Build order description for Square
  const getOrderDescription = () => {
    return `Tailtations ${WHOLESALE.kit.size}-Pack Wholesale Kit`
  }

  // Process payment
  const handleProcessPayment = async (paymentMethod: 'card' | 'apple' | 'google') => {
    if (!reservation) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title="No Reservation"
          message="No reservation found. Please try again."
          type="error"
        />
      ))
      return
    }

    // Check if reservation has already expired before starting payment
    const expiresAt = new Date(reservation.expires_at).getTime()
    if (Date.now() >= expiresAt) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title="Reservation Expired"
          message="Your reservation has expired. Please go back and try again."
          type="error"
        />
      ))
      clearQuote()
      setStep('select')
      return
    }

    setIsProcessing(true)

    try {
      let paymentResult

      if (paymentMethod === 'apple') {
        paymentResult = await processApplePayment(Math.round(totalAmount * 100), reservation.reservation_id)
      } else if (paymentMethod === 'google') {
        paymentResult = await processGooglePayment(Math.round(totalAmount * 100), reservation.reservation_id)
      } else {
        paymentResult = await processCardPayment(Math.round(totalAmount * 100), reservation.reservation_id)
      }

      if (paymentResult?.errors) {
        const errorMessage = handleSquareError(paymentResult.errors)
        throw new Error(errorMessage)
      }

      // Create the wholesale order
      const convertResponse = await fetch('/.netlify/functions/convert-reservation-to-wholesale-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reservationId: reservation.reservation_id,
          email: customerEmail.toLowerCase().trim(),
          fullName: customerName.trim(),
          kitSize: selectedKit?.size,
          kitPrice: (selectedKit?.price ?? 0) * quantity,
          totalAmount,
          shippingAddress,
          state: shippingAddress.state,
          zip: shippingAddress.zip,
          shippingCost: quote?.shippingCost ?? 0,
          taxAmount: quote?.taxAmount ?? 0,
          sku: WHOLESALE.kit.sku,
          quantity,
          couponCode: couponCode || undefined,
          discountAmount: quote?.discountAmount ?? 0,
          deliveryMethod,
        }),
      })

      if (!convertResponse.ok) {
        const errorData = await convertResponse.json()
        throw new Error(errorData.error || 'Failed to create order')
      }

      const { order_id, order_number } = await convertResponse.json()
      setOrderNumber(order_number)

      const paymentResponse = await fetch('/.netlify/functions/process-wholesale-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceId: paymentResult?.token,
          amount: totalAmount,
          orderId: order_id,
          email: customerEmail.toLowerCase().trim(),
          verificationToken: paymentResult?.verificationToken,
          orderDescription: getOrderDescription(),
          customerTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      })

      const paymentData = await paymentResponse.json().catch(() => null)

      if (!paymentResponse.ok || !paymentData?.success || !paymentData?.paymentId) {
        throw new Error(paymentData?.error || 'Payment processing failed')
      }

      setStep('success')

      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title="Order Confirmed!"
          message={`Confirmation sent to ${customerEmail}`}
          type="success"
        />
      ))
    } catch (error: any) {
      console.error('Payment processing error:', error)
      const errorMessage = error.message || 'Unable to process payment. Please try again.'
      setPaymentError(errorMessage)

      // Only send the user back to the start when the RESERVATION itself
      // expired/converted (the convert step returns RESERVATION_EXPIRED for
      // "expired"/"converted"/"not found"). A payment decline is recoverable —
      // keep the user on the payment step so they can retry their card. The
      // word "reservation" alone is too broad: a Square decline mentioning it
      // would wrongly nuke the whole flow.
      const lower = errorMessage.toLowerCase()
      if (lower.includes('reservation has expired') || lower.includes('reservation_expired')) {
        clearQuote()
        setStep('select')
      }

      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title="Payment Failed"
          message={errorMessage}
          type="error"
        />
      ))
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Wholesale Fried Lobster Tails for Restaurants &amp; Foodservice — Tailtations</title>
        <meta name="description" content="Wholesale fried lobster tails for restaurants, hotels, caterers, and foodservice operators. The 30-Pack Wholesale Kit: hand-cracked, blast-chilled, zero-prep. Plate consistently without skilled labor — $375 for 30 tails, shipped frozen via UPS 2-Day." />
      </Helmet>

      <div
        className="wholesale-page min-h-screen relative"
        style={{ scrollBehavior: 'smooth', backgroundColor: '#000000', color: '#ffffff' }}
      >
        {/* Subtle Background Pattern */}
        <div
          className="fixed inset-0 opacity-5 pointer-events-none z-0"
          style={{
            backgroundImage: `url(${repeatBg})`,
            backgroundRepeat: 'repeat',
            backgroundSize: '200px',
          }}
        />

        {/* Global Header */}
        <header
          className="py-4 px-4 md:py-8 md:px-8 border-b border-white/10 relative z-10"
          style={{ backgroundColor: 'transparent' }}
        >
          <div className="container mx-auto flex justify-center items-center max-w-7xl">
            <a href="/" target="_blank" rel="noopener noreferrer">
              <img
                src={logoWhite}
                alt="Tailtations Fried Lobster Brand"
                className="w-[180px] md:w-[250px] h-auto"
              />
            </a>
          </div>
        </header>

        {/* STEP: SELECT — Landing Page + Product Selection */}
        {step === 'select' && (
          <>

            {/* Section 1 — Hero */}
            <section
              className="relative w-full min-h-screen min-h-[100dvh] flex items-center z-10 overflow-hidden md:bg-cover md:bg-center py-8"
              style={{ backgroundColor: '#0a0a0a' }}
            >
              {/* Mobile background image + scrim (hidden on desktop) */}
              <div
                className="absolute inset-0 md:hidden"
                style={{
                  backgroundImage: `url(${heroImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <div className="absolute inset-0 bg-black/55 md:hidden" />

              <div className="relative z-10 w-full container mx-auto px-6 max-w-7xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 items-center">
                  {/* Text column */}
                  <div className="text-center md:text-left">
                    <h1 className="text-5xl md:text-7xl font-bold !text-white mb-6 font-heading leading-tight">
                      Not Your Average
                      <br />
                      Lobster.
                    </h1>
                    <p className="text-xl md:text-2xl !text-white/60 mb-10 font-body max-w-3xl mx-auto md:mx-0 leading-relaxed">
                      Hand-cracked. Famous Taste. Blast-chilled. This is the tail that changes the game.
                      The only lobster you can serve consistently without skilled labor.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        document.getElementById('kit-selector-anchor')?.scrollIntoView({ behavior: 'smooth' })
                      }}
                      className="bg-[#C13340] hover:bg-[#962831] !text-white font-bold py-4 px-8 md:py-5 md:px-14 rounded-xl text-xl md:text-2xl font-heading transition-all duration-300 transform hover:scale-105 shadow-[0_0_40px_rgba(193,51,64,0.5)]"
                    >
                      Order Wholesale Kit &rarr;
                    </button>
                  </div>

                  {/* Image column — desktop only */}
                  <div className="hidden md:flex items-center justify-center">
                    <img
                      src={heroImage}
                      alt="Tailtations gourmet deep-fried lobster tail with gold flake"
                      className="w-full max-h-[80vh] object-contain drop-shadow-2xl"
                      style={{ borderRadius: '50px' }}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2 — The Process (3-column) */}
            <section className="relative z-10" style={{ backgroundColor: '#0d0d0d' }}>
              <div className="container mx-auto px-6 py-8 max-w-6xl">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                  {/* The Craft */}
                  <div className="text-center">
                    <div className="rounded-xl overflow-hidden mb-6 aspect-[4/3]">
                      <img src={prepImage} alt="Chef hand-dipping lobster tail in batter" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-2xl font-bold !text-white mb-3 font-heading tracking-wider">THE CRAFT</h3>
                    <p className="!text-white/70 font-body leading-relaxed">
                      Every tail is hand-dipped in our signature batter by the Tailtations kitchen team before it ever sees heat. Zero prep required on the restaurant line.
                    </p>
                  </div>

                  {/* The Cook */}
                  <div className="text-center">
                    <div className="rounded-xl overflow-hidden mb-6 aspect-[4/3]">
                      <img src={cookImage} alt="Lobster tail being lifted from fryer" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-2xl font-bold !text-white mb-3 font-heading tracking-wider">THE COOK</h3>
                    <p className="!text-white/70 font-body leading-relaxed">
                      Deep-fried to perfection in a commercial fryer, ensuring consistent, repeatable quality that your back-of-house can execute flawlessly during a rush.
                    </p>
                  </div>

                  {/* The Lock */}
                  <div className="text-center">
                    <div className="rounded-xl overflow-hidden mb-6 aspect-[4/3]">
                      <img src={preserveImage} alt="Cooked tails in blast chiller" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-2xl font-bold !text-white mb-3 font-heading tracking-wider">THE LOCK</h3>
                    <p className="!text-white/70 font-body leading-relaxed">
                      Our industrial blast chiller drops each tail to -68&deg;F in record time, preserving the structure and locking in every drop of succulent juice for up to eight months.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 3 — Product Specifications Table */}
            <WholesaleSpecsTable />

            {/* Section 4 — Shipping Estimates */}
            <WholesaleShippingEstimator />

            {/* Section 5 — Product Selection */}
            <section id="product-selection" className="relative z-10" style={{ backgroundColor: '#111111' }}>
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: `url(${repeatBg})`,
                  backgroundRepeat: 'repeat',
                  opacity: 0.08,
                  mixBlendMode: 'multiply'
                }}
              />
              <div className="container mx-auto px-6 py-8 max-w-6xl relative z-10">
                {/* Offer Header with Image */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16 items-center">
                  <div>
                    <h2 className="text-4xl md:text-5xl font-bold !text-white mb-6 font-heading leading-tight">
                      The Tail That Changes the Game.
                    </h2>
                    <p className="text-lg !text-white/70 mb-4 font-body leading-relaxed">
                      Tailtations has been the best-kept secret behind the most exclusive events in Los Angeles. The flavor that people don't forget — now available as a labor-saving, high-margin product for your kitchen.
                    </p>
                    <p className="text-lg !text-white/70 font-body leading-relaxed">
                      No skilled labor required. Consistent, repeatable quality. Straight from frozen to fryer in 5 minutes. The only lobster your back-of-house can execute flawlessly during a rush.
                    </p>
                  </div>
                  <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                    <img src={offerImage} alt="Tailtations branded packaging" className="w-full h-auto" />
                  </div>
                </div>

                <div id="kit-selector-anchor" />

                {/* Kit Selector */}
                <WholesaleKitSelector />

                {/* Order Summary */}
                {selectedKit && (
                  <div className="max-w-md mx-auto mb-8">
                    <WholesaleOrderSummary />
                  </div>
                )}
              </div>
            </section>

            {/* Section 6 — FAQ */}
            <WholesaleFaq />

            {/* Spacer at page end so the fixed buy bar never covers the footer */}
            {selectedKit && <div className="h-24"></div>}

            {/* Persistent sticky buy bar */}
            {selectedKit && (
              <div className="fixed bottom-0 left-0 right-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-white/10 shadow-lg z-50" style={{ backgroundColor: '#111111' }}>
                <div className="container mx-auto max-w-7xl flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="hidden md:block">
                    <span className="font-bold font-heading text-lg !text-white">
                      {quantity} × {selectedKit.label} Wholesale Kit
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleContinueToDetails}
                    className="w-full md:w-auto bg-[#962831] hover:bg-[#7a2029] !text-white font-bold py-4 md:py-4 px-8 md:px-14 rounded-xl text-lg md:text-xl shadow-lg md:transform md:hover:scale-105 transition-all duration-300"
                  >
                    Secure Your {selectedKit.label} Kit{quantity > 1 ? `s` : ''} &rarr;
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* STEP: DETAILS — Customer Info + Shipping */}
        {step === 'details' && (
          <main className="w-full relative z-10" style={{ backgroundColor: '#111111' }}>
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: `url(${repeatBg})`,
                backgroundRepeat: 'repeat',
                opacity: 0.08,
                mixBlendMode: 'multiply'
              }}
            />
            <div className="container mx-auto px-4 py-16 max-w-2xl relative z-10">
              <h2 className="text-4xl font-bold !text-white mb-10 text-center font-heading">
                Your Information
              </h2>

              {/* Summary Card */}
              <div className="bg-gradient-to-br from-[#962831] to-[#6B2A2A] rounded-2xl shadow-2xl p-6 md:p-8 mb-10">
                <div className="text-center">
                  <div className="text-lg mb-2 !text-white">Your Order</div>
                  <div className="text-3xl md:text-4xl font-bold mb-3 !text-white">
                    {quantity} × {selectedKit?.label} Wholesale Kit
                  </div>
                  <div className="text-4xl md:text-5xl font-bold mb-3 !text-white">${totalAmount.toFixed(2)}</div>
                </div>
              </div>

              {/* Email Input */}
              <div id="email-input-section" className="bg-white/5 border border-white/10 rounded-2xl shadow-xl p-5 md:p-8 mb-6">
                <label className="block text-sm font-semibold !text-white/70 mb-2">
                  Email Address <span className="!text-red-500">*</span>
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={customerEmail}
                  onChange={(e) => {
                    setCustomerEmail(e.target.value)
                    setEmailError(null)
                  }}
                  placeholder="your@email.com"
                  className={`w-full px-4 py-4 border-2 rounded-xl focus:outline-none bg-white/10 !text-white text-lg placeholder-white/40 ${
                    emailError
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-white/20 focus:border-[#962831]'
                  }`}
                />
                {emailError && <p className="mt-2 text-sm text-red-500">{emailError}</p>}
                <p className="mt-2 text-sm !text-white/40">Required for your order confirmation</p>
              </div>

              {/* Full Name */}
              <div className="bg-white/5 border border-white/10 rounded-2xl shadow-xl p-5 md:p-8 mb-6">
                <label className="block text-sm font-semibold !text-white/70 mb-2">
                  Full Name <span className="!text-red-500">*</span>
                </label>
                <input
                  type="text"
                  autoComplete="name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="First and Last Name"
                  className="w-full px-4 py-4 border-2 border-white/20 rounded-xl focus:border-[#962831] focus:outline-none bg-white/10 !text-white text-lg placeholder-white/40"
                />
              </div>

              {/* Shipping Address */}
              <div className="bg-white/5 border border-white/10 rounded-2xl shadow-xl p-5 md:p-8 mb-6">
                <h3 className="text-lg font-bold !text-white mb-4">Shipping Address</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold !text-white/70 mb-1">
                      Street Address <span className="!text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      autoComplete="address-line1"
                      value={shippingAddress.street}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, street: e.target.value })}
                      placeholder="123 Main St"
                      className={`w-full px-4 py-3 text-base border-2 rounded-xl focus:outline-none bg-white/10 !text-white placeholder-white/40 ${
                        addressErrors.street ? 'border-red-500' : 'border-white/20 focus:border-[#962831]'
                      }`}
                    />
                    {addressErrors.street && <p className="mt-1 text-sm text-red-500">{addressErrors.street}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold !text-white/70 mb-1">
                      Apt / Suite / Unit
                    </label>
                    <input
                      type="text"
                      autoComplete="address-line2"
                      value={shippingAddress.apt}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, apt: e.target.value })}
                      placeholder="Apt 4B"
                      className="w-full px-4 py-3 text-base border-2 border-white/20 rounded-xl focus:border-[#962831] focus:outline-none bg-white/10 !text-white placeholder-white/40"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold !text-white/70 mb-1">
                        City <span className="!text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        autoComplete="address-level2"
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                        placeholder="Los Angeles"
                        className={`w-full px-4 py-3 text-base border-2 rounded-xl focus:outline-none bg-white/10 !text-white placeholder-white/40 ${
                          addressErrors.city ? 'border-red-500' : 'border-white/20 focus:border-[#962831]'
                        }`}
                      />
                      {addressErrors.city && <p className="mt-1 text-sm text-red-500">{addressErrors.city}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold !text-white/70 mb-1">
                        State <span className="!text-red-500">*</span>
                      </label>
                      <select
                        value={shippingAddress.state}
                        autoComplete="address-level1"
                        onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                        className={`w-full px-4 py-3 text-base border-2 rounded-xl focus:outline-none bg-white/10 !text-white placeholder-white/40 ${
                          addressErrors.state ? 'border-red-500' : 'border-white/20 focus:border-[#962831]'
                        }`}
                      >
                        <option value="">Select</option>
                        {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {addressErrors.state && <p className="mt-1 text-sm text-red-500">{addressErrors.state}</p>}
                    </div>
                  </div>

                  <div className="w-1/2">
                    <label className="block text-sm font-semibold !text-white/70 mb-1">
                      ZIP Code <span className="!text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      value={shippingAddress.zip}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, zip: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                      onBlur={() => fetchQuote()}
                      placeholder="90001"
                      maxLength={5}
                      className={`w-full px-4 py-3 text-base border-2 rounded-xl focus:outline-none bg-white/10 !text-white placeholder-white/40 ${
                        addressErrors.zip ? 'border-red-500' : 'border-white/20 focus:border-[#962831]'
                      }`}
                    />
                    {addressErrors.zip && <p className="mt-1 text-sm text-red-500">{addressErrors.zip}</p>}
                    {quoteLoading && !addressErrors.zip && (
                      <p className="mt-1 text-sm !text-white/50">Updating shipping…</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Allergen Notice */}
              <div className="bg-red-950/30 border border-red-800/50 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 !text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm !text-red-300">
                    <p className="font-semibold mb-1 !text-inherit">Allergen Notice</p>
                    <p className="!text-inherit">Our products contain <strong>shellfish (lobster)</strong>, gluten, paprika, and garlic. If you have a food allergy, please contact us before ordering.</p>
                  </div>
                </div>
              </div>

              {/* Shipping Info */}
              <div className="bg-blue-950/30 border border-blue-800/50 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <Package className="w-5 h-5 !text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm !text-blue-300">
                    <p className="font-semibold mb-1 !text-inherit">Shipping Info</p>
                    <p className="!text-inherit">We ship to all 50 U.S. states + D.C. No P.O. Boxes. Products arrive flash-frozen in insulated Uline shipper packaging with dry ice via UPS 2-Day Air. Someone must be available at your business to receive the package on the delivery day.</p>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="mb-10">
                <WholesaleOrderSummary />
                {quote?.shippingEstimated && (
                  <p className="mt-3 text-xs !text-white/50 text-center">
                    Shipping estimated for your area.
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="fixed bottom-0 left-0 right-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-white/10 shadow-lg md:relative md:p-0 md:pb-0 md:bg-transparent md:border-0 md:shadow-none z-50" style={{ backgroundColor: '#111111' }}>
                <div className="flex space-x-4 max-w-2xl mx-auto">
                  <button
                    type="button"
                    onClick={() => { clearQuote(); setStep('select') }}
                    className="flex-1 bg-white/10 hover:bg-white/20 !text-white font-bold py-4 px-6 rounded-xl transition-colors text-lg"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleContinueToPayment}
                    disabled={isCreatingReservation}
                    className="flex-1 bg-[#962831] hover:bg-[#6B2A2A] !text-white font-bold py-4 px-6 rounded-xl transition-colors text-lg disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isCreatingReservation ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 !text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Continue to Payment \u2192'
                    )}
                  </button>
                </div>
              </div>
              {/* Spacer for fixed button on mobile */}
              <div className="h-24 md:hidden"></div>
            </div>
          </main>
        )}

        {/* STEP: PAYMENT */}
        {step === 'payment' && reservation && (
          <main className="w-full relative z-10" style={{ backgroundColor: '#111111' }}>
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: `url(${repeatBg})`,
                backgroundRepeat: 'repeat',
                opacity: 0.08,
                mixBlendMode: 'multiply'
              }}
            />
            <div className="container mx-auto px-4 py-16 max-w-2xl relative z-10">
              <h2 className="text-4xl font-bold !text-white mb-10 text-center font-heading">
                Complete Your Order
              </h2>

              <ReservationTimer expiresAt={reservation.expires_at} onExpire={handleReservationExpire} />

              {/* Payment Summary */}
              <div className="bg-gradient-to-br from-[#962831] to-[#6B2A2A] rounded-2xl shadow-2xl p-6 md:p-8 mb-10">
                <div className="text-center mb-5">
                  <div className="text-lg mb-1 !text-white/80">Total Amount</div>
                  <div className="text-4xl md:text-5xl font-bold !text-white">${totalAmount.toFixed(2)}</div>
                </div>

                {/* Itemized breakdown */}
                <div className="bg-black/15 rounded-xl p-4 md:p-5 space-y-2 text-sm md:text-base">
                  <div className="flex justify-between">
                    <span className="!text-white/80">
                      {selectedKit?.label} Wholesale Kit
                    </span>
                    <span className="font-semibold !text-white">
                      ${(quote ? quote.subtotal : (selectedKit?.price || 0)).toFixed(2)}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="!text-white/80">
                      Shipping{shippingAddress.zip ? ` to ${shippingAddress.zip}` : ''}
                      <span className="block text-xs !text-white/55">
                        {deliveryMethod === 'local' ? 'Local Courier' : 'UPS 2-Day'}
                      </span>
                    </span>
                    <span className="font-semibold !text-white">
                      {quote
                        ? quote.discountAmount > 0
                          ? (
                            <>
                              <span className="!text-white/50 line-through mr-2 font-normal">
                                ${(quote.shippingCost + quote.discountAmount).toFixed(2)}
                              </span>
                              ${quote.shippingCost.toFixed(2)}
                            </>
                          )
                          : `$${quote.shippingCost.toFixed(2)}`
                        : '—'}
                    </span>
                  </div>

                  {quote && quote.discountAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="!text-emerald-300">
                        Discount
                        {quote.couponCode ? (
                          <span className="block text-xs !text-emerald-300/70">
                            {deliveryMethod === 'local' ? 'Free local delivery' : 'Free shipping'} · {quote.couponCode}
                          </span>
                        ) : null}
                      </span>
                      <span className="font-semibold !text-emerald-300">
                        −${quote.discountAmount.toFixed(2)}
                      </span>
                    </div>
                  )}

                  {quote && quote.taxAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="!text-white/80">Sales Tax</span>
                      <span className="font-semibold !text-white">${quote.taxAmount.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="border-t border-white/20 pt-2 mt-1 flex justify-between">
                    <span className="font-bold !text-white">Total</span>
                    <span className="font-bold !text-white">${totalAmount.toFixed(2)}</span>
                  </div>

                  {quote?.shippingEstimated && (
                    <p className="text-xs !text-white/55 pt-1">Shipping estimated for your area.</p>
                  )}
                </div>

                {customerEmail && (
                  <div className="mt-4 text-center text-sm !text-white/80">Confirmation will be sent to: {customerEmail}</div>
                )}
              </div>

              {/* Payment Form */}
              <div className="bg-white/5 border border-white/10 rounded-2xl shadow-xl p-5 md:p-8">
                <WholesalePaymentForm
                  containerId="wholesale-card-container"
                  onPaymentComplete={() => handleProcessPayment('card')}
                  onApplePayComplete={() => handleProcessPayment('apple')}
                  onGooglePayComplete={() => handleProcessPayment('google')}
                  isProcessing={isProcessing}
                  buttonText={`Pay $${totalAmount.toFixed(2)}`}
                />
              </div>
            </div>
          </main>
        )}

        {/* STEP: SUCCESS */}
        {step === 'success' && orderNumber && (
          <main className="w-full relative z-10" style={{ backgroundColor: '#111111' }}>
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: `url(${repeatBg})`,
                backgroundRepeat: 'repeat',
                opacity: 0.08,
                mixBlendMode: 'multiply'
              }}
            />
            <div className="container mx-auto px-4 py-16 max-w-[800px] relative z-10">
              {/* Main Confirmation Card */}
              <div className="bg-gradient-to-br from-[#962831] to-[#6B2A2A] rounded-2xl shadow-2xl p-6 md:p-12 mb-8 text-center">
                <div className="flex items-center justify-center mb-6">
                  <Package className="w-16 h-16 !text-white" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold !text-white mb-6 font-heading">
                  Your Wholesale Order Is In.
                  <br />
                  Welcome to the Inner Circle.
                </h2>
                <p className="text-lg md:text-xl !text-white leading-relaxed">
                  Thank you for your order. Your kitchen is now part of an exclusive group of operators serving the tail that changes the game.
                </p>
              </div>

              {/* Order Details Card */}
              <div className="bg-white/5 border-2 border-[#DAA520] rounded-xl shadow-lg p-5 md:p-8 mb-8">
                <div className="text-center mb-6">
                  <div className="text-xs font-semibold tracking-wide uppercase !text-white/50 mb-2">
                    Order Number
                  </div>
                  <div className="text-2xl md:text-3xl font-bold !text-white">
                    {orderNumber}
                  </div>
                </div>

                <div className="border-t border-white/10 my-6"></div>

                {/* Order Summary */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="!text-white/60">{selectedKit?.label} Wholesale Kit</span>
                    <span className="font-semibold !text-white">${selectedKit?.price.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-white/10 pt-3">
                    <div className="flex justify-between text-xl font-bold">
                      <span className="!text-white">Total Charged</span>
                      <span className="!text-white">${totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="text-sm font-semibold !text-white/50 mb-2">Ships To</div>
                  <div className="!text-white">
                    <div className="!text-white">{customerName}</div>
                    <div className="!text-white">{shippingAddress.street}{shippingAddress.apt ? `, ${shippingAddress.apt}` : ''}</div>
                    <div className="!text-white">{shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}</div>
                  </div>
                </div>

                <div className="border-t border-white/10 my-6"></div>

                <p className="text-sm !text-white/70 text-center mb-4">
                  Your order is confirmed and will ship via UPS 2-Day Air in an insulated Uline shipper with dry ice. We'll send tracking info to <span className="font-semibold !text-white">{customerEmail}</span> the moment it leaves our kitchen.
                </p>

                <div className="bg-blue-950/30 border border-blue-800/50 rounded-lg p-4 text-sm !text-blue-300">
                  <p className="font-semibold mb-1 !text-inherit">Cancellation Policy</p>
                  <p className="!text-inherit">You may cancel within 48 hours of purchase for a full refund by emailing <a href="mailto:contact@tailtations.com" className="underline font-medium">contact@tailtations.com</a>. After 48 hours, all sales are final.</p>
                </div>
              </div>

              {/* Refer a fellow operator */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 md:p-8 mb-8 text-center">
                <p className="text-xl font-bold !text-white mb-2 font-heading">
                  Know another operator who'd run this?
                </p>
                <p className="text-sm !text-white/60 mb-4">
                  Refer a fellow restaurant, hotel, or caterer to Tailtations Wholesale.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                  <a
                    href="mailto:?subject=Tailtations%20Wholesale%20Lobster%20Tails&body=I%20just%20added%20Tailtations%20fried%20lobster%20tails%20to%20our%20menu%20%E2%80%94%20zero-prep%2C%20blast-chilled%2C%20and%20consistent%20every%20time.%20Wholesale%20kits%20here%3A%20https%3A%2F%2Ftailtations.com%2Fwholesale"
                    className="inline-flex items-center justify-center gap-2 bg-[#962831] hover:bg-[#7a2029] !text-white font-semibold py-3 px-6 rounded-lg transition-opacity"
                  >
                    <Share2 className="w-4 h-4" />
                    Refer an Operator
                  </a>
                  <a
                    href="mailto:contact@tailtations.com?subject=Wholesale%20Reorder%20%2F%20Account"
                    className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 !text-white font-semibold py-3 px-6 rounded-lg transition-opacity"
                  >
                    Set Up Reorders
                  </a>
                </div>
              </div>

              {/* Return to Homepage CTA */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="bg-[#962831] hover:bg-[#7D4545] !text-white font-semibold py-4 px-8 rounded-lg transition-all duration-200 ease-in-out transform hover:scale-105 text-base"
                >
                  Return to Homepage
                </button>
              </div>
            </div>
          </main>
        )}

        {/* Global Footer */}
        <Footer />
      </div>
    </>
  )
}

export default WholesalePage
